import styled, { css, keyframes } from 'styled-components';

export default function Card({
  text,
  color,
  active = false,
  sizeScale = 1,
  stackTarget = false,
}: {
  text: string;
  color?: string;
  active?: boolean;
  sizeScale?: number;
  stackTarget?: boolean;
}) {
  return (
    <CardFrame
      $active={active}
      $sizeScale={sizeScale}
      $stackTarget={stackTarget}
    >
      <CardBacking />
      <CardColor $color={color} />
      <CardText>{text}</CardText>
    </CardFrame>
  );
}

const pulseRing = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(121, 114, 254, 0.4);
  }
  70% {
    box-shadow: 0 0 0 6px rgba(121, 114, 254, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(121, 114, 254, 0);
  }
`;

const CardFrame = styled.div<{
  $active: boolean;
  $sizeScale?: number;
  $stackTarget?: boolean;
}>`
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  border-width: 2px;
  border-radius: 8px;
  border-color: ${(props) => (props.$stackTarget ? '#7a6ff2' : '#000')};
  color: #000;
  font-weight: 500;
  font-family: 'Aronui';
  user-select: none;

  min-width: calc(42px * ${(props) => props.$sizeScale || 1});
  height: calc(42px * ${(props) => props.$sizeScale || 1});
  font-size: calc(16px * ${(props) => props.$sizeScale || 1});
  padding: calc(6px * ${(props) => props.$sizeScale || 1})
    calc(8px * ${(props) => props.$sizeScale || 1});

  @media screen and (min-width: 1080px) {
    min-width: calc(
      60px * ${(props) => props.$sizeScale || 1}
        ${(props) => (props.$active ? '' : '*0.8')}
    );
    height: calc(
      60px * ${(props) => props.$sizeScale || 1}
        ${(props) => (props.$active ? '' : '*0.8')}
    );
    font-size: calc(
      22px * ${(props) => props.$sizeScale || 1}
        ${(props) => (props.$active ? '' : '*0.8')}
    );
    padding: calc(8px * ${(props) => props.$sizeScale || 1})
      calc(12px * ${(props) => props.$sizeScale || 1});
  }

  @media screen and (min-width: 1280px) {
    min-width: calc(
      84px * ${(props) => props.$sizeScale || 1}
        ${(props) => (props.$active ? '' : '*0.6')}
    );
    height: calc(
      84px * ${(props) => props.$sizeScale || 1}
        ${(props) => (props.$active ? '' : '*0.6')}
    );
    font-size: calc(
      32px * ${(props) => props.$sizeScale || 1}
        ${(props) => (props.$active ? '' : '*0.6')}
    );
    padding: calc(12px * ${(props) => props.$sizeScale || 1})
      calc(16px * ${(props) => props.$sizeScale || 1});
  }

  transition: all 100ms;

  ${(props) =>
    props.$stackTarget &&
    css`
      transform: scale(0.95);
      animation: ${pulseRing} 1s ease-out infinite;
      border-width: 3px;
    `}

  &:hover {
    transform: scale(1.05) translateY(-1px);
    box-shadow: 0 3px 8px rgba(0, 0, 0, 0.12);
  }

  &:active {
    transform: scale(1.02) translateY(-0.5px);
    transition: transform 0.1s cubic-bezier(0.68, -0.55, 0.265, 1.55);
  }
`;

const CardBacking = styled.div`
  position: absolute;
  z-index: 0;
  background-color: white;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
`;

const CardColor = styled.div<{ $color?: string }>`
  position: absolute;
  z-index: 1;
  background-color: ${(props) =>
    props.$color ? props.$color : 'hsla(var(--primary), 0.5)'};
  opacity: 0.5;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
`;

const CardText = styled.div`
  position: relative;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
`;
